import { defineStore } from "pinia";
import { reactive, ref } from "vue";
import instanceJWT from "../../api/apiJwt";
import type { Detail } from "../../models/Detail";
import type { PluginStatus } from "../../models/PluginStatus";
import { generateUUID } from "../../common/helpers/generateUUID";
import i18n from "../../../i18n";
import { createPollingActions } from "./eplexSearchPolling";
import { createSuggestActions } from "./eplexSearchSuggest";
import { createCartActions } from "./eplexSearchCart";
import type { TabState, VehicleNameInfo } from "../../common/helpers/eplex/eplexTabsHelper";
import { filterValidPluginIds } from "../../common/helpers/eplex/multiSearchHelpers";
import { resolveSearchBrand } from "../../common/helpers/eplex/searchBrand";

export { firstBrandToken } from "../../common/helpers/eplex/searchBrand";

interface SearchState {
  details: Detail[];
  brandsAliases: Record<string, string[]>;
  loading: boolean;
  results: any[];
  searchValue: string;
  searchStatuses: Record<number, PluginStatus>;
  requestId: string;
  time_from: string;
  isSearching: boolean;
  debugInfo: any;
  quickFilterValue: string;
  selectedBrand: string | null;
  selectedBrid: string | null;
  cartQuantity: number;
  addedItems: Record<number, number>;
  isImagePanelVisible: boolean;
  isNewSearch: boolean;
  gridState: any;
  progress: number;
  // Поколение поиска вкладки (CAB-586). Инкрементируется при старте нового поиска,
  // применении фильтров и остановке. Любой асинхронный ответ поллинга, у которого
  // токен на момент старта не совпадает с текущим, отбрасывается — так результаты
  // отменённого/устаревшего поиска не могут дописаться в стор.
  searchToken: number;
  // Поиск остановлен пользователем (CAB-586). Пока true — вкладку не опрашиваем и не
  // возобновляем поллинг; requestId и уже найденные результаты сохраняются (корзина,
  // заморозка на экране). Сбрасывается при старте нового поиска / применении фильтров.
  stopped: boolean;
  // Бэк удалил результаты этой проценки по сроку хранения (error_code 407 в
  // 500-конверте). Не сбой, а устаревшие данные: тост не показываем, а пустую
  // таблицу подписываем объяснением (CAB-602 п.4).
  resultsExpired: boolean;
  // Идёт перечитывание выдачи под новый фильтр. Это не поиск: поставщиков не опрашиваем,
  // но человеку показываем, что запрос ушёл, — иначе движение ползунка выглядит как
  // «ничего не произошло».
  filtering: boolean;
  // Какому фильтру соответствует то, что сейчас в results. Нужен, чтобы отличать «выдача
  // меньше, потому что фильтр строже» от «выдача меньше, потому что бэкенд ещё не собрал
  // её целиком» (см. smartFilterKey в eplexSearchPolling).
  resultsFilter: string;
  vehicleMetadata: {
    car?: {
      maker?: string;
      model?: string;
      year?: string;
      engine?: string;
      transmission?: string;
      vin?: string;
      plateNumber?: string;
    };
    title?: string;
  } | null;
  showVehicleMetadata: boolean;
  // схема узла из каталога (dynamic form): картинка + выделение конкретной детали;
  // показывается в карточке товара. rect — координаты детали в пикселях исходной картинки
  scheme: {
    url: string;
    width: number;
    height: number;
    rect: { x: number; y: number; width: number; height: number } | null;
  } | null;
  // id исходного отказанного заказа при перепроценке из уведомления: прокидывается
  // в позицию при добавлении в корзину (reorder_id) — бэкенд связывает старый и новый заказ.
  reorderId: string | null;
}

const tabActive = ref<Record<number, boolean>>({});

export const useSingleSearchStore = defineStore("singleSearch", () => {
  const tabs = ref<TabState[]>([{ id: 1, title: i18n.global.t("singleSearch.defaultTabTitle", { n: 1 }) }]);
  const searches = reactive<Record<number, SearchState>>({});
  // Активная вкладка проценки (ставит EplexMultiSearch). Нужна туру: вторая
  // часть шага «Первая проценка» показывается, только если результаты открыты
  // именно в текущей вкладке, а не где-то в сторе (CAB-636, замечание 8).
  const activeTabId = ref<number | null>(null);
  const vehicleNames = reactive<Record<string, VehicleNameInfo>>({});
  const selectedPlugins = ref<Record<number, number[]>>({});
  const selectedGroups = ref<Record<number, string>>({});
  const pollingIntervals = ref<Record<number, number>>({});
  const pollingActive = ref<Record<number, boolean>>({});

  function createTabState(): SearchState {
    return reactive({
      details: [],
      brandsAliases: {},
      loading: false,
      isSearching: false,
      results: [],
      searchStatuses: {},
      requestId: "",
      time_from: "",
      searchValue: "",
      debugInfo: {},
      quickFilterValue: "",
      selectedBrand: null,
      selectedBrid: null,
      cartQuantity: 0,
      addedItems: {},
      isImagePanelVisible: false,
      isNewSearch: true,
      gridState: null,
      progress: 0,
      searchToken: 0,
      stopped: false,
      resultsExpired: false,
      filtering: false,
      resultsFilter: "",
      vehicleMetadata: null,
      showVehicleMetadata: false,
      scheme: null,
      reorderId: null,
    }) as SearchState;
  }

  function ensureTabState(tabId: number) {
    if (!tabId) return;
    searches[tabId] ??= createTabState();
  }

  const deltaCache = new Map<
    number,
    {
      results?: any[];
      searchStatuses?: Record<number, PluginStatus>;
      debugInfo?: any;
    }
  >();

  function cacheDeltaForTab(tabId: number, data: any) {
    let cached = deltaCache.get(tabId) ?? {};
    if (Array.isArray(data.results)) {
      cached.results = data.results;
    }
    if (data.info?.debug?.plugins) {
      cached.searchStatuses = { ...cached.searchStatuses };
      const pluginsResult = data.info.debug.plugins;
      for (const key in pluginsResult) {
        const plugin = pluginsResult[key];
        const pluginId = Number(key);
        if (pluginId === -1) continue;
        if (plugin.err) {
          cached.searchStatuses[pluginId] = { icon: "error", error: plugin.err };
        } else if (plugin.time_exec) {
          cached.searchStatuses[pluginId] = { icon: "success" };
        } else {
          cached.searchStatuses[pluginId] = { icon: "search" };
        }
      }
    }
    if (data.info?.debug) {
      cached.debugInfo = data.info.debug;
    }
    deltaCache.set(tabId, cached);
  }

  function clearDeltaForTab(tabId: number) {
    deltaCache.delete(tabId);
  }

  const polling = createPollingActions({
    searches,
    pollingActive,
    pollingIntervals,
    tabActive,
    cacheDeltaForTab,
    clearDeltaForTab,
  });

  const suggest = createSuggestActions(searches, ensureTabState);

  const cart = createCartActions(searches, ensureTabState);

  const initializeSearchStatuses = (selectedPluginIds: number[]): Record<number, PluginStatus> => {
    const nextStatuses: Record<number, PluginStatus> = {};
    for (const p of selectedPluginIds) {
      nextStatuses[p] = { icon: "search" };
    }
    return nextStatuses;
  };

  const buildSearchRequestBody = (
    client_id: string,
    selectedDetail: any,
    searchValue: string,
    selectedPluginIds: number[],
    tab: SearchState,
    car?: Record<string, unknown> | null,
  ): any => {
    const requestBody: any = {
      client_id,
      filter: { article_only: false },
      plugins: selectedPluginIds.map(Number),
      timeout: 60,
      brands: "",
      searchstring: searchValue,
    };

    if (car) requestBody.car = car;
    // Явный выбор всех брендов не подменяем авто, даже при оставшемся brid.
    if (selectedDetail?.isAllBrandsRow) return requestBody;

    if (selectedDetail?.brid) {
      const brandArray = tab.brandsAliases[selectedDetail.brid] ?? [];
      requestBody.brands = brandArray.join("/");
      requestBody.detail_id = selectedDetail.id;
      requestBody.searchstring = selectedDetail.art;
    }

    // Алиасы выбранной детали приоритетны. Без них — бренд, затем марка авто.
    if (!requestBody.brands) {
      requestBody.brands = resolveSearchBrand({ brand: selectedDetail?.br, maker: car?.maker }) ?? "";
    }

    return requestBody;
  };

  const updateLaunchStatuses = (tabId: number, launchedPlugins: number[], selectedPluginIds: number[]) => {
    const launchedNumeric = launchedPlugins.map(Number);
    const selArr = selectedPluginIds.map(Number);

    selArr.forEach((pid: number) => {
      if (!launchedNumeric.includes(pid)) {
        searches[tabId].searchStatuses[pid] = {
          icon: "warning",
          error: i18n.global.t("errors.pluginNotStarted"),
        };
      } else {
        searches[tabId].searchStatuses[pid] ??= { icon: "search" };
      }
    });
  };

  const handleSearchLiveError = (tabId: number) => {
    searches[tabId].loading = false;
    searches[tabId].progress = 0;
    searches[tabId].isSearching = false;
    Object.keys(searches[tabId].searchStatuses).forEach((pluginIdStr) => {
      const pluginId = Number(pluginIdStr);
      searches[tabId].searchStatuses[pluginId] = {
        icon: "error",
        error: i18n.global.t("errors.searchStartFailed"),
      };
    });
  };

  const searchLive = async (
    tabId: number,
    selectedDetail: any,
    searchValue: string,
    selectedPluginIds: number[],
    car?: Record<string, unknown> | null,
    pollInterval = 3000,
  ): Promise<void> => {
    polling.stopPollingForTab(tabId);
    ensureTabState(tabId);

    searches[tabId].searchValue = searchValue;
    searches[tabId].debugInfo = {};
    searches[tabId].results = [];
    // выдачи ещё нет — и она не отвечает никакому фильтру
    searches[tabId].resultsFilter = "";
    searches[tabId].searchStatuses = {};
    searches[tabId].progress = 0;
    searches[tabId].showVehicleMetadata = true;

    if (searches[tabId].isSearching === false) {
      searches[tabId].isSearching = true;
    } else {
      return;
    }
    // Новое поколение поиска: инвалидирует любые in-flight поллы прежней сессии и снимает
    // отметку «остановлен» с предыдущего поиска этой вкладки (CAB-586)
    searches[tabId].searchToken += 1;
    searches[tabId].stopped = false;
    searches[tabId].resultsExpired = false;
    searches[tabId].loading = true;
    searches[tabId].isSearching = true;

    searches[tabId].searchStatuses = initializeSearchStatuses(selectedPluginIds);
    const client_id = generateUUID();

    const requestBody = buildSearchRequestBody(
      client_id,
      selectedDetail,
      searchValue,
      selectedPluginIds,
      searches[tabId],
      car,
    );

    // searchstring обязателен для search_live: с пустой строкой бэк отвечает 500
    // «Пустая строка поиска» (например, «машинная» запись истории без артикула)
    if (!requestBody.searchstring?.trim()) {
      console.error("searchLive: empty searchstring, search aborted");
      handleSearchLiveError(tabId);
      return;
    }

    selectedPlugins.value[tabId] ??= selectedPluginIds.map(Number);

    try {
      const resp = await instanceJWT.post("eplex/search_live", requestBody);
      const resultData = resp.data?.data ?? {};
      const request_id = resultData.request_id;
      const time_from = resultData.time_from;

      if (!request_id) {
        throw new Error("no request_id in search_live response");
      }

      searches[tabId].requestId = request_id;
      searches[tabId].time_from = time_from ?? "";

      updateLaunchStatuses(tabId, resultData.plugin_launched ?? [], selectedPluginIds);

      await polling.pollOnce(tabId);

      if (tabActive.value[tabId]) {
        polling.startSequentialPolling(tabId, () => polling.pollOnce(tabId), pollInterval);
      }
    } catch (err) {
      console.error("Failed to start search_live", err);
      handleSearchLiveError(tabId);
    }
  };

  function flushDeltaCache(tabId: number) {
    const cached = deltaCache.get(tabId);
    if (!cached) return;

    const tab = searches[tabId];
    if (!tab) return;

    if (cached.results) {
      tab.results = [...cached.results];
    }
    if (cached.searchStatuses) {
      tab.searchStatuses = { ...tab.searchStatuses, ...cached.searchStatuses };
    }
    if (cached.debugInfo) {
      tab.debugInfo = cached.debugInfo;

      const debug = cached.debugInfo;
      const totalStarted = debug.totalStarted ?? debug.total_started ?? 0;
      const totalSuccess = debug.totalSuccess ?? debug.total_success ?? 0;
      const totalError = debug.totalError ?? debug.total_error ?? 0;

      if (totalStarted > 0) {
        tab.progress = Math.min(100, Math.round(((totalSuccess + totalError) / totalStarted) * 100));
      }

      polling.stopPollingIfComplete(tabId, debug);
    }

    deltaCache.delete(tabId);
  }

  const updateTabInterval = (tabId: number, pollInterval: number) => {
    try {
      polling.stopPollingForTab(tabId);
      // остановленный поиск не возобновляем (CAB-586)
      if (searches[tabId]?.requestId && !searches[tabId]?.stopped) {
        polling.startSequentialPolling(tabId, () => polling.pollOnce(tabId), pollInterval);
      }
    } catch (e) {
      console.error("[store] failed to update interval", e);
    }
  };

  function setTabActive(tabId: number, isActive: boolean) {
    tabActive.value[tabId] = isActive;

    if (isActive) {
      flushDeltaCache(tabId);
    }
  }

  const clearAll = () => {
    Object.keys(pollingIntervals.value).forEach((key) => {
      polling.stopPollingForTab(Number(key));
    });

    Object.keys(searches).forEach((key) => {
      searches[Number(key)].gridState = null;
    });

    Object.keys(searches).forEach((key) => {
      delete searches[Number(key)];
    });

    selectedPlugins.value = {};
    selectedGroups.value = {};

    tabs.value = [{ id: 1, title: i18n.global.t("singleSearch.defaultTabTitle", { n: 1 }) }] as TabState[];

    tabActive.value = {};

    deltaCache.clear();
  };

  /**
   * Пересобирает выбор поставщиков во всех вкладках под актуальный каталог (CAB-696).
   *
   * Нужно тем вкладкам, что уже открыты: сверка на монтировании страницы им не
   * поможет, а каталог меняется и под открытой страницей — выход из демо-доступа
   * забирает демо-поставщиков. Без этого в панели оставались отмеченными площадки,
   * которых у клиента больше нет, и проценка уходила по ним в пустоту.
   */
  const revalidateSelectedPlugins = (availableIds: number[]) => {
    for (const key of Object.keys(selectedPlugins.value)) {
      const tabId = Number(key);
      selectedPlugins.value[tabId] = filterValidPluginIds(selectedPlugins.value[tabId] ?? [], availableIds);
    }
  };

  const saveGridStateForTab = (tabId: number, state: any) => {
    ensureTabState(tabId);
    searches[tabId].gridState = { ...state };
  };

  const loadGridStateForTab = (tabId: number) => {
    ensureTabState(tabId);
    return searches[tabId].gridState ?? null;
  };

  function saveDynamicColumnsState(key: string, state: any) {
    localStorage.setItem(key, JSON.stringify(state));
  }

  function loadDynamicColumnsState(key: string) {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : null;
  }

  const definedNameFields = (info: VehicleNameInfo): Partial<VehicleNameInfo> => {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(info)) {
      if (typeof value === "string" && value.trim() !== "") out[key] = value;
    }
    return out as Partial<VehicleNameInfo>;
  };

  // запоминаем имя авто в реестре, как только марка стала известна (каталог/ИИ/история);
  // не затираем уже заполненные поля пустыми — копим максимально полную карточку
  const recordVehicleName = (car: any) => {
    const maker = typeof car?.maker === "string" ? car.maker.trim() : "";
    if (!maker) return;
    const fields = definedNameFields({
      maker,
      model: car.model,
      year: car.year,
      engine: car.engine,
      transmission: car.transmission,
      vin: car.vin,
      plateNumber: car.plateNumber,
    });
    if (car.vin) vehicleNames[car.vin] = { ...vehicleNames[car.vin], ...fields } as VehicleNameInfo;
    if (car.plateNumber)
      vehicleNames[car.plateNumber] = { ...vehicleNames[car.plateNumber], ...fields } as VehicleNameInfo;
  };

  const loadVehicleNames = (saved: Record<string, VehicleNameInfo> | undefined) => {
    if (!saved) return;
    Object.keys(saved).forEach((key) => {
      if (saved[key]?.maker) vehicleNames[key] = { ...vehicleNames[key], ...saved[key] };
    });
  };

  const setVehicleMetadata = (tabId: number, metadata: any) => {
    ensureTabState(tabId);

    const currentMetadata = searches[tabId].vehicleMetadata;

    if (currentMetadata) {
      const mergedCar = metadata?.car ? { ...currentMetadata?.car, ...metadata.car } : currentMetadata?.car;
      searches[tabId].vehicleMetadata = {
        ...metadata,
        vin: metadata?.vin ?? currentMetadata.car?.vin,
        plateNumber: metadata?.plateNumber ?? currentMetadata.car?.plateNumber,
        title: metadata?.title ?? currentMetadata.title,
        car: mergedCar,
      };
    } else {
      searches[tabId].vehicleMetadata = metadata;
    }

    recordVehicleName(searches[tabId].vehicleMetadata?.car);
  };

  const clearVehicleMetadata = (tabId: number) => {
    ensureTabState(tabId);
    searches[tabId].vehicleMetadata = null;
  };

  function setActiveTabId(tabId: number | null): void {
    activeTabId.value = tabId;
  }

  return {
    tabs,
    searches,
    activeTabId,
    setActiveTabId,
    vehicleNames,
    recordVehicleName,
    loadVehicleNames,
    pollingIntervals,
    selectedPlugins,
    selectedGroups,
    revalidateSelectedPlugins,
    fetchDetails: suggest.fetchDetails,
    clearDetails: suggest.clearDetails,
    fetchSuggest: suggest.fetchSuggest,
    suggestLoading: suggest.suggestLoading,
    stopSearch: polling.stopSearch,
    searchLive,
    fetchResultsWithFilters: polling.fetchResultsWithFilters,
    fetchItemInfo: cart.fetchItemInfo,
    addToCart: cart.addToCart,
    incrementCartQuantity: cart.incrementCartQuantity,
    incrementAddedItem: cart.incrementAddedItem,
    decrementItemCount: cart.decrementItemCount,
    decrementCartQuantity: cart.decrementCartQuantity,
    incrementItemCount: cart.incrementItemCount,
    removeAddedItem: cart.removeAddedItem,
    clearAll,
    setTabActive,
    updateTabInterval,
    stopPollingForTab: polling.stopPollingForTab,
    // отложенная дельта неактивной вкладки — чистится при закрытии вкладки, иначе при
    // переиспользовании id новая вкладка влила бы чужие результаты через flushDeltaCache (CAB-586)
    clearDeltaForTab,
    ensureTabState,
    pollOnce: polling.pollOnce,
    saveGridStateForTab,
    loadGridStateForTab,
    saveDynamicColumnsState,
    loadDynamicColumnsState,
    setVehicleMetadata,
    clearVehicleMetadata,
  };
});
